import { useEffect, useMemo, useState } from 'react'
import AccordionTAG from '../AccordionTAG'
import AddMaterialForm from '../forms/AddMaterialForm'
import { Box, Button, Typography } from '@mui/material'
import { useForm, useWatch } from 'react-hook-form'
import colors from '../../constants/colors'
import CardTAG from '../CardTAG'
import constants from '../CaseInvestigation/constants'
import { ICaseOverviewForm } from '../CaseOverview/types'
import DividerTAG from '../DividerTAG'
import FormLayout from '../forms/FormLayout'
import utils from './utils'
import { resolveNumberOfChangesString } from '../CaseOverview/utils'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from '../../store'
import useCallbackPrompt from '../../hooks/useCallbackPrompt'
import ModalTAG from '../ModalTAG'
import {
  ICasetypeAnswers,
  ICasetypeQuestions,
  IQuestionV2,
  MainObject,
} from '../../store/reports/types'
import reportsSelectors from '../../store/reports/reportsSelectors'
import { IPOSTMaterial } from '../../store/materials/types'
import {
  addAdditionalMaterial,
  getReportAdditionalMaterial,
  patchMaterial,
  patchMaterialFile,
} from '../../store/materials/materialSlice'
import materialSelectors from '../../store/materials/materialSelectors'
import { uploadInputZones } from '../UploadDropzone/types'
import {
  postAnswersToCaseTypeQuestions,
  resetNumberOfChanges,
  setDefaultAnswersState,
  setInitialAnswers,
  setInitialValueToAnswer,
  updateAnswerFiles,
} from '../../store/reports/reportsSlice'
import reportsService from '../../services/reportsService'
import { ICaseInvestigationDataPart } from './types'
import materialsService from '../../services/materialsService'
import questionsService from '../../services/questionsService'
import SubmitCancelButtons from '../SubmitCancelButtons'
import AdditionalMaterialCard from '../AdditionalMaterialCard'
import { useLocation } from 'react-router-dom'
import authSelectors from '../../store/auth/authSelectors'

import { success, error } from '../../utils/alertUtils';

const DataPart: React.FC<ICaseInvestigationDataPart> = ({
  answersFiles,
  setAnswersFiles,
  archivedCase,
}) => {
  const [changes, setChanges] = useState(0)
  const [openModalAreYouSure, setOpenModalAreYouSure] = useState(false)
  const [archivedAnswers, setArchivedAnswers] = useState<ICasetypeAnswers[]>([])
  const [newMaterialFiles, setNewMaterialFiles] = useState<{ id: number; files: File[] }[]>([])
  const [materialFilesToDelete, setMaterialFilesToDelete] = useState<
    { id: number; files: string[] }[]
  >([])
  const [haveQuestionsBeenRendered, setHaveQuestionsBeenRendered] = useState<boolean>(false)
  const [haveWeScrolledDowntoQuestion, setHaveWeScrolledDowntoQuestion] = useState<boolean>(false)
  const selectedCaseTypesQuestions = useSelector(reportsSelectors.caseTypeQuestions)
  const selectedReport = useSelector(reportsSelectors.selectedReport)
  const reportAdditionalMaterials = useSelector(materialSelectors.getAdditionalMaterials)
  const selectedReportTypes = useSelector(reportsSelectors.reportCaseTypes)
  const numberOfChanges = useSelector(reportsSelectors?.caseQuestionsNumberOfChanges)
  const userEditAccess = useSelector(authSelectors.userReportEditAccess)
  const dispatch = useDispatch<AppDispatch>()

  const formMethods = useForm<ICaseOverviewForm>({
    defaultValues: constants.formState,
    mode: 'onChange',
    shouldFocusError: false,
  })
  const { control, register, unregister, handleSubmit, setValue, formState, reset } = formMethods

  const handleConfirm = async (data: any) => {
    try {
      if (data?.additionalMaterials?.length) {
        await dispatch(addAdditionalMaterial({ data: data.additionalMaterials })).unwrap();
      }

      const uploadedFileMap = new Map<number, number[]>();
      const materialsToUpdate = new Set<number>();

      if (newMaterialFiles.length > 0) {
        const validPendingUploads = newMaterialFiles
          .map(item => ({
            id: item.id,
            files: item.files.filter(f => f instanceof File && f.size > 0)
          }))
          .filter(item => item.files.length > 0 && Number.isFinite(item.id));

        if (validPendingUploads.length > 0) {
          const uploadResults = await Promise.all(
            validPendingUploads.flatMap(({ id, files }) =>
              files.map(async (file) => {
                const formData = new FormData();
                formData.append('attachment', file);
                const { data } = await materialsService.postFile(formData);
                return { materialId: id, fileId: data.id };
              })
            )
          );

          uploadResults.forEach(({ materialId, fileId }) => {
            if (!uploadedFileMap.has(materialId)) uploadedFileMap.set(materialId, []);
            uploadedFileMap.get(materialId)!.push(fileId);
          });

          validPendingUploads.forEach(({ id }) => materialsToUpdate.add(id));
        }
      }

      const deletedFileIds = new Set<number>();

      if (materialFilesToDelete.length > 0) {
        const deletePromises = materialFilesToDelete.flatMap(({ id, files }) =>
          files
            .filter(fileId => typeof fileId === 'number' || /^\d+$/.test(fileId))
            .map(fileId => {
              const numericId = Number(fileId);

              return materialsService.deleteFile(numericId)
                .then(() => {
                  deletedFileIds.add(numericId);
                  console.log(`File ${numericId} successfully deleted`);
                })
                .catch(err => {
                  // Gracefully handle "already deleted" (404)
                  if (
                    err?.response?.status === 404 ||
                    err?.detail?.includes?.('No File matches the given query')
                  ) {
                    console.info(`File ${numericId} was already deleted – treating as success`);
                    deletedFileIds.add(numericId); // Still mark as deleted in local state
                  } else {
                    console.error(`Failed to delete file ${numericId}`, err);
                    throw err; // Let real errors bubble up
                  }
                });
            })
        );

        // Actually await all deletions!
        await Promise.all(deletePromises);
      }

      // Mark materials that had deletions for later update/soft-delete
      materialFilesToDelete.forEach(({ id }) => {
        if (Number.isFinite(id)) materialsToUpdate.add(id);
      });

      // for (const materialId of materialsToUpdate) {
      //   const material = reportAdditionalMaterials?.find(m => m.id === materialId);
      //   if (!material) continue;

      //   const remainingIds = material.attachments
      //     ?.filter(att => !deletedFileIds.has(att.id))
      //     .map(att => att.id)
      //     ?? [];

      //   const newIds = uploadedFileMap.get(materialId) ?? [];
      //   const finalFileIds = [...remainingIds, ...newIds];

      //   if (finalFileIds.length > 0) {
      //     await dispatch(
      //       patchMaterialFile({
      //         materialId,
      //         fileId: finalFileIds,
      //       })
      //     ).unwrap();
      //   }
      //   // Optional: soft-delete empty materials
      //   else {
      //     await dispatch(patchMaterial({ fileId: materialId, material: { is_deleted: true } })).unwrap();
      //   }
      // }
      for (const materialId of materialsToUpdate) {
        const material = reportAdditionalMaterials?.find(m => m.id === materialId);
        if (!material) continue;

        const remainingIds = material.attachments
          ?.filter(att => !deletedFileIds.has(att.id))
          .map(att => att.id) ?? [];

        const newIds = uploadedFileMap.get(materialId) ?? [];
        const finalFileIds = [...remainingIds, ...newIds];

        // OPTIMAL BEHAVIOR:
        // If no file IDs exist, SKIP. Do NOT PATCH. Do NOT SOFT DELETE.
        if (finalFileIds.length === 0) {
          console.log(`Material ${materialId}: 0 files — skipping patch entirely.`);
          continue;
        }

        // Patch ONLY when actual file IDs are present
        try {
          await dispatch(
            patchMaterialFile({
              materialId,
              fileId: finalFileIds,
            })
          ).unwrap();
        } catch (err: any) {
          const msg =
            err?.response?.data?.detail ||
            err?.detail ||
            err?.message ||
            '';

          // Ignore "No File..." gracefully
          if (msg.includes('No File matches')) {
            console.info(
              `Material ${materialId}: file IDs already removed — ignoring.`
            );
            continue;
          }

          throw err; // other errors still bubble up
        }
      }


      if (selectedReport?.id) {
        dispatch(getReportAdditionalMaterial({ reportId: selectedReport.id }));
      }

      setNewMaterialFiles([]);
      setMaterialFilesToDelete([]);
      setChanges(0);
      dispatch(resetNumberOfChanges({}));
      reset();
      success('All changes saved perfectly!');

    } catch (err: any) {
      console.error('Save failed:', err);
      error('Failed to save. Please try again.');
    }
  };

  const additionalMaterials = useWatch({
    control,
    name: 'additionalMaterials', // without supply name will watch the entire form, or ['firstName', 'lastName'] to watch both
    defaultValue: [], // default value before the render
  })

  const addFileToAdditionalMaterial = (materialId: number, file: File) => {
    setNewMaterialFiles(prev => {
      if (prev?.length === 0) {
        return [{ id: materialId, files: [file] }]
      }

      const idAleradyExist = prev?.find(file => file?.id === materialId)
      if (idAleradyExist) {
        return prev?.map(material => {
          if (material?.id === materialId) {
            return {
              ...material,
              files: [...(material?.files?.length > 0 ? material.files : []), file],
            }
          }
          return material
        })
      }

      return [...prev, { id: materialId, files: [file] }]
    })
  }
  const removeUnsavedFile = (materialId: number, file: File) => {
    setNewMaterialFiles(prev =>
      prev.map(item =>
        item.id === materialId
          ? { ...item, files: item.files.filter(f => f !== file) }
          : item
      )
    );
  };

  const deleteAdditionalMaterials = (identifier: any) => {
    if (Number.isFinite(identifier)) {
      dispatch(patchMaterial({ fileId: identifier, material: { is_deleted: true } }))
      return
    }
    const newFiles = additionalMaterials.filter((x: { name: string }) => x?.name !== identifier)
    if (newFiles.length === 0) {
      unregister('additionalMaterials')
      return
    }
    setValue('additionalMaterials', newFiles, { shouldDirty: true })
  }

  const discardChangesLogic = () => {
    reset()
    setChanges(0)
    dispatch(resetNumberOfChanges({}))
    success('Changes discarded');
  }

  const saveEditsLogic = async () => {
    // eslint-disable-next-line
    // console.log('FINAL SAVE')
    if (answersFiles?.length > 0) {
      const filePromises = answersFiles
        .filter(item => item?.files.length > 0)
        .map(answerFile => {
          return answerFile?.files?.map(async (file: File) => {
            const formData = new FormData()
            formData.append('attachment', file)
            return await materialsService.postFile(formData).then(res => {
              return { questionId: answerFile?.uuid, fileId: res.data?.id }
            })
          })
        })
        .flat(1)

      try {
        const response = await Promise.all(filePromises)
        const data = [...response] as { questionId: string; fileId: number }[]
        data?.forEach(item => {
          dispatch(updateAnswerFiles({ answerUUID: item?.questionId, files: item?.fileId }))
        })
      } catch (error) {
        // eslint-disable-next-line
        console.log('Err:', error)
      }
    }

    handleSubmit(handleConfirm)()
    dispatch(postAnswersToCaseTypeQuestions())
      .then(_ => {
        success('Changes saved');
        setChanges(0)
        setOpenModalAreYouSure(false)
      })
      .catch(_ => {
        error('Error saving changes')
      })
  }

  const [showPrompt, confirmNavigation, cancelNavigation, justClosePrompt] = useCallbackPrompt(
    openModalAreYouSure,
    discardChangesLogic,
    saveEditsLogic,
  )

  const renderQuestions = (data: IQuestionV2[]) => {
    if (!data) {
      return []
    }
    const a = data.map((question: IQuestionV2) =>
      utils.questionParser(question, control, unregister, setValue, setAnswersFiles),
    )
    return a
  }

  const testFunction = async () => {
    if (selectedReportTypes) {
      const test = selectedReportTypes.map(item => item?.text)
      const aaaPromises = test.map(async (caseType: string) => {
        return await reportsService.getAnswersToCaseTypeQuestions(
          selectedReport?.id as number,
          caseType,
        )
      })
      
      const caseTypeAnswers = await Promise.all(aaaPromises).then(res => {
        return res
          ?.map(response => response?.data)
          .filter(data => data?.length > 0)
          .flat(1)
      })
      
      // Recursively initsubanswers
      const initSubanswers = (element: any) => {
        element?.subanswers?.forEach((subAnswer : any) => {
          if (subAnswer?.subanswers && subAnswer?.subanswers?.length > 0) {
            initSubanswers(subAnswer)
          }
          dispatch(
            setInitialValueToAnswer({
              answerUUID: subAnswer?.question?.uuid,
              answer: subAnswer,
            }),
          )
        })
      }
      caseTypeAnswers.forEach(element => {
        dispatch(
          setInitialValueToAnswer({
            answerUUID: element?.question?.uuid,
            answer: element,
          }),
        )
        if (element.subanswers) {
          initSubanswers(element)
        } 
      })
      dispatch(setDefaultAnswersState({}))
    }
  }

  const getArchivedQuestions = async () => {
    if (selectedReport) {
      const { data } = await questionsService.getArchivedQuestions(selectedReport?.id)
      const answers = data as ICasetypeAnswers[]
      setArchivedAnswers(answers.filter(answer => answer.type !== 'OSHA'))
    }
  }

  const archivedSection = useMemo(() => {
    if (!archivedAnswers || !archivedAnswers.length) {
      return <></>
    }

    return archivedAnswers.map((item: ICasetypeAnswers, index: any) => {
      if (item.type === 'OSHA') {
        return <></>
      }
      const questions = item.answers.map(answer => {
        if (answer?.subanswers?.length > 0) {
          return {
            ...answer?.question,
            subquestions: answer?.subanswers.map(subAnswer => subAnswer.question),
            options: [answer?.answers[0].question_option],
          }
        }
        return {
          ...answer?.question,
          options: [answer?.answers[0].question_option],
        }
      })

      const answers = questions?.map(question => utils.transformStructure(question, item.type))
      let updatedAnswers = [...answers]

      item.answers.forEach(answer => {
        updatedAnswers = [
          ...utils.updateAnswersOptions(updatedAnswers, answer?.question?.uuid, answer),
        ] as any[]
      })

      return (
        <AccordionTAG
          boxError={false}
          title={constants.sections[item?.type]?.title}
          key={`${item?.type}${index}`}
          color={'tagBoxArchive'}
        >
          <FormLayout>
            {questions.map((question: any) => {
              return utils.questionParser(
                question,
                control,
                unregister,
                setValue,
                setAnswersFiles,
                updatedAnswers as unknown as MainObject[],
                true,
              )
            })}
          </FormLayout>
        </AccordionTAG>
      )
    })
  }, [archivedAnswers])

  // const renderedSections = useMemo(() => {
  //   if (!selectedCaseTypesQuestions || !selectedCaseTypesQuestions.length) {
  //     return <></>
  //   }
  //   const sectionsToReturn = selectedCaseTypesQuestions.map(
  //     (item: ICasetypeQuestions, index: any) => {
  //       return (
  //         <div className='marginTop-sections' key={`${item?.caseType}${index}`} id={item?.caseType}>
  //           <AccordionTAG boxError={false} title={constants.sections[item?.caseType]?.title}>
  //             <FormLayout>{renderQuestions(item?.questions)}</FormLayout>
  //           </AccordionTAG>
  //         </div>
  //       )
  //     },
  //   )

  //   setHaveQuestionsBeenRendered(true)
  //   return sectionsToReturn
  // }, [selectedCaseTypesQuestions])

  const renderedSections = useMemo(() => {
    if (!selectedCaseTypesQuestions?.length) return null;

    return selectedCaseTypesQuestions.map((item, index) => (
      <div className="marginTop-sections" key={`${item?.caseType}${index}`} id={item?.caseType}>
        <AccordionTAG boxError={false} title={constants.sections[item?.caseType]?.title}>
          <FormLayout>{renderQuestions(item?.questions)}</FormLayout>
        </AccordionTAG>
      </div>
    ));
  }, [selectedCaseTypesQuestions]);

  useEffect(() => {
    if (selectedCaseTypesQuestions?.length) {
      setHaveQuestionsBeenRendered(true);
    }
  }, [selectedCaseTypesQuestions]);


  // const answersToCaseTypeQuestions = useMemo(() => {
  //   const test = utils.getAnswersToCaseTypeQuestions(selectedCaseTypesQuestions)
  //   dispatch(setInitialAnswers(test))
  //   testFunction()
  // }, [selectedCaseTypesQuestions])

  useEffect(() => {
  const test = utils.getAnswersToCaseTypeQuestions(selectedCaseTypesQuestions);
  dispatch(setInitialAnswers(test));
  testFunction();
}, [selectedCaseTypesQuestions]);

  const renderedAdditionalMaterials = useMemo(() => {
    let newArray: any[] = []

    if (reportAdditionalMaterials && reportAdditionalMaterials?.length > 0 && !archivedCase) {
      const filteredData = reportAdditionalMaterials.filter(material => {
        if (material.type === uploadInputZones.TEXT_FIELD) return true
        if (material.type === uploadInputZones.LINK) return true
        return material?.attachments?.filter(file => file?.is_deleted === false)
      })
      newArray = newArray.concat(filteredData)
    }
    if (additionalMaterials?.length > 0 ? additionalMaterials : []) {
      newArray = newArray.concat(additionalMaterials)
    }
    if (newMaterialFiles?.length > 0) {
      newArray = newArray?.map(material => {
        const item = newMaterialFiles?.find(newFile => newFile?.id === material?.id)
        if (item) {
          return {
            ...material,
            attachments: [
              ...(material?.attachments.length > 0 ? material.attachments : []),
              ...(item?.files?.length > 0 ? item.files : []),
            ],
          }
        }
        return material
      })
    }

    if (materialFilesToDelete?.length > 0) {
      newArray = newArray?.map(material => {
        const item = materialFilesToDelete?.find(
          materialFiles => materialFiles?.id === material?.id,
        )
        if (item) {
          return {
            ...material,
            // attachments: material?.attachments?.filter((attachment: { id: string }) => {
            //   return !item?.files?.some(file => file === attachment?.id)
            // }),
            attachments: material.attachments.filter(
              (attachment: { id: number | string }) => {
                const attId = String(attachment.id);
                return !item.files.some(fileId => String(fileId) === attId);
              }
            ),
          }
        }
        return material
      })
    }

    if (newArray?.length !== 0) {
      // const whatToRender = utils.renderUploadedFiles(newArray, deleteAdditionalMaterials, false)
      const whatToRender = newArray?.map(material => {
        return (
          <div key={material?.id ?? Math.random()}>
            <AdditionalMaterialCard
              material={material}
              setNewMaterialFiles={addFileToAdditionalMaterial}
              removeUnsavedFile={removeUnsavedFile}
              setMaterialFilesToDelete={setMaterialFilesToDelete}
            />
          </div>
        )
      })
      return whatToRender
    }

    return <></>
  }, [reportAdditionalMaterials, additionalMaterials, newMaterialFiles, materialFilesToDelete])

  useEffect(() => {
    if (selectedReport?.id && !reportAdditionalMaterials) {
      dispatch(getReportAdditionalMaterial({ reportId: selectedReport.id as unknown as number }))
    }
  }, [selectedReport])

  useEffect(() => {
    getArchivedQuestions()
  }, [])

  useEffect(() => {
    setOpenModalAreYouSure(!!calculateNumberOfChanges())
    setChanges(numberOfChanges)
  }, [numberOfChanges, additionalMaterials])

  const { hash } = useLocation()

  // useEffect(() => {
  //   if (!haveWeScrolledDowntoQuestion && haveQuestionsBeenRendered) {
  //     setHaveWeScrolledDowntoQuestion(true)
  //     if (hash && hash.length > 1) {
  //       const id = hash.slice(1);
  //       const element = document.getElementById(id);
  //       if (element) {
  //         window.scrollTo({ behavior: 'smooth', top: element.getBoundingClientRect().top - document.body.getBoundingClientRect().top - 150 });
  //       }
  //     }
  //     // const element = document.getElementById(`${hash.slice(1)}`)
  //     // if (element) {
  //     //   window.scrollTo({
  //     //     behavior: 'smooth',
  //     //     top:
  //     //       element.getBoundingClientRect().top - document.body.getBoundingClientRect().top - 150,
  //     //   })
  //     // }
  //   }
  // })

  useEffect(() => {
    if (!haveWeScrolledDowntoQuestion && haveQuestionsBeenRendered) {
      setHaveWeScrolledDowntoQuestion(true);
      if (hash && hash.length > 1) {
        const id = hash.slice(1);
        const element = document.getElementById(id);
        if (element) {
          window.scrollTo({
            behavior: 'smooth',
            top: element.getBoundingClientRect().top - document.body.getBoundingClientRect().top - 150
          });
        }
      }
    }
  }, [hash, haveWeScrolledDowntoQuestion, haveQuestionsBeenRendered]);

  const calculateNumberOfChanges = () => {
    const additionalMaterialsNumberOfChanges = additionalMaterials.length

    return numberOfChanges + additionalMaterialsNumberOfChanges
  }

  return (
    <form onSubmit={handleSubmit(handleConfirm)}>
      {!archivedCase && (
        <>
          <Box className={'t-custom-accordion'}>
            <span className={'t-custom-accordion-title t-color-blue'}>Questions</span>
          </Box>
          <DividerTAG border='1px' color={colors.TAG_BLUE} />
          {renderedSections}
          <Box className={'t-custom-accordion t-purple'}>
            <span className={'t-custom-accordion-title t-color-purple'}>Additional Materials</span>
          </Box>
          <DividerTAG border='1px' color={colors.TAG_PURPLE} />
        </>
      )}

      <FormLayout>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '3.6rem',
            width: '100%',
            paddingBottom: { md: '4rem', xl: '5rem' },
          }}
        >
          {Boolean(archivedCase || userEditAccess) && (
            <AddMaterialForm
              title={archivedCase ? 'Add Case Updates' : undefined}
              control={control}
              fieldName={'additionalMaterials'}
              color='tagBoxMaterial'
              setValue={setValue}
            />
          )}
          {(additionalMaterials.length !== 0 || reportAdditionalMaterials?.length !== 0) && (
              <FormLayout>{renderedAdditionalMaterials}</FormLayout>
            )}
        </Box>
        {!archivedCase && archivedAnswers.length > 0 && (
          <>
            <Box sx={{ borderBottom: `2px, solid ${colors.BLACK}`, paddingBottom: '1rem' }}>
              <Typography variant='h1' color={colors.BLACK}>
                Archived Questions
              </Typography>
            </Box>
            <DividerTAG border='2px' color={colors.BLACK} />
            {archivedSection}
          </>
        )}
      </FormLayout>
      {Boolean(archivedCase || userEditAccess) && (
        <SubmitCancelButtons
          submitButtonText={`Save \n ${resolveNumberOfChangesString(calculateNumberOfChanges())}`}
          submitHandler={saveEditsLogic}
          cancelButtonText='Cancel'
          cancelHandler={discardChangesLogic}
        />
      )}
      <ModalTAG open={showPrompt as boolean} style={{ width: 'auto' }}>
        <CardTAG
          title='Unsaved Edits'
          boxError={true}
          closable={true}
          closeCard={justClosePrompt as any}
        >
          <Typography variant='bodyBold' color={colors.TAG_RED}>
            You have {calculateNumberOfChanges()} unsaved Edits
          </Typography>
          <Typography variant='bodyBold'>
            Would you like to save your changes before leaving this page?
          </Typography>
          <Box sx={{ display: 'flex', gap: '3rem', width: '100%', paddingTop: '3rem' }}>
            <Button
              onClick={confirmNavigation as any}
              variant='submitFormButton'
              sx={{ backgroundColor: colors.GREEN, color: colors.WHITE, flex: '50%' }}
            >
              <Typography variant='bodyBold'>Save my Edits</Typography>
            </Button>
            <Button onClick={cancelNavigation as any} variant='outlined' sx={{ flex: '50%' }}>
              <Typography variant='bodyRegular' color='inherit'>
                Discard My Edits
              </Typography>
            </Button>
          </Box>
        </CardTAG>
      </ModalTAG>
    </form>
  )
}

export default DataPart
